import React, { useEffect, useState } from 'react';
import {
    Tabs,
    Tab,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    TextField,
    Typography,
    Divider,
    FormControl,
    Autocomplete,
    InputAdornment,
    IconButton
} from '@mui/material';
import { SquarePen, Save, Lock } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import api from '../axios/api';
import { showNotification } from '../redux/slices/notificationSlice';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';

// Profile form validation schema
const profileSchema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email format').required('Email is required'),
    countryId: yup.string().required('Country is required'),
    phoneNo: yup.string()
        .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
        .required('Phone number is required'),
});

// Password form validation schema
const passwordSchema = yup.object().shape({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: yup.string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters')
        .matches(
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
            'Password must include uppercase, lowercase, number, and special character'
        ),
    confirmPassword: yup.string()
        .required('Password confirmation is required')
        .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

const MyProfile = () => {
    const [isEditing, setIsEditing] = useState(false);
    const user = useSelector((state) => state.user.user);
    const [countries, setCountries] = useState([]);
    const [tabValue, setTabValue] = useState(0);
    const dispatch = useDispatch();
    const [showPassword, setShowPassword] = useState({
        currentPassword: false,
        newPassword: false,
        confirmPassword: false,
    });

    const handleTogglePassword = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    // Profile form
    const { control: profileControl, handleSubmit: handleProfileSubmit, reset: resetProfile, formState: { errors: profileErrors } } = useForm({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            countryId: '',
            phoneNo: ''
        }
    });

    // Password form
    const { control: passwordControl, handleSubmit: handlePasswordSubmit, reset: resetPassword, formState: { errors: passwordErrors } } = useForm({
        resolver: yupResolver(passwordSchema),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
        }
    });

    const onProfileSubmit = async (data) => {
        try {
            await api.put(`/auth/edituser/`, {
                id: user.id,
                ...data
            });
            setIsEditing(false);
            dispatch(
                showNotification({
                    message: 'Profile updated successfully',
                    type: "success",
                })
            );
        } catch (error) {
            dispatch(
                showNotification({
                    message: error.response?.data?.message || 'Error updating profile',
                    type: "error",
                })
            );

        }
    };

    const handleReset = () => {
        resetPassword();
    };

    const onPasswordSubmit = async (data) => {
        try {
            await api.post('/auth/resetPassword', {
                currentPassword: data.currentPassword,
                newPassword: data.newPassword
            });

            resetPassword();
            dispatch(
                showNotification({
                    message: 'password updated successfully',
                    type: "success",
                })
            );
        } catch (error) {
            dispatch(
                showNotification({
                    message: error.response?.data?.error || 'Error resetting password',
                    type: "error",
                })
            );
        }
    };

    const fetchUserDetails = async () => {
        try {
            const response = await api.get(`/auth/user/${user.id}`);
            const userData = response.data.data;
            resetProfile({
                firstName: userData.first_name,
                lastName: userData.last_name,
                email: userData.email,
                countryId: userData.country_id,
                phoneNo: userData.phone_no
            });
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    const fetchCountries = async () => {
        try {
            const response = await api.get('/common/countrydrop');
            setCountries(response.data.data);
        } catch (error) {
            console.error('Error fetching countries:', error);
        }
    };

    useEffect(() => {
        fetchUserDetails();
        fetchCountries();
    }, []);

    return (
        <Box sx={{ maxWidth: 700, mx: 'auto', p: 4, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 3 }}>
            <Tabs
                value={tabValue}
                onChange={(e, newValue) => setTabValue(newValue)}
                variant="fullWidth"
                sx={{ mb: 3, borderRadius: 1 }}
            >
                <Tab label="Profile" sx={{ fontWeight: 'bold' }} />
                <Tab label="Reset Password" sx={{ fontWeight: 'bold' }} />
            </Tabs>

            {tabValue === 0 && (
                <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <CardHeader
                        title="Profile Information"
                        action={
                            <div onClick={() => { setIsEditing(!isEditing) }} className='p-2 cursor-pointer'>
                                <SquarePen color='white' />
                            </div>
                        }
                        sx={{
                            color: 'white',
                            borderRadius: '8px 8px 0 0',
                            p: 2,
                        }}
                        className="bg-green-600 text-white"
                    />

                    <Divider />
                    <CardContent sx={{ p: 3 }}>
                        <Box component="form" display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={3}>
                            <Controller
                                name="firstName"
                                control={profileControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                First Name
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        disabled={!isEditing}
                                        error={!!profileErrors.firstName}
                                        helperText={profileErrors.firstName?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Controller
                                name="lastName"
                                control={profileControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                Last Name
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        disabled={!isEditing}
                                        error={!!profileErrors.lastName}
                                        helperText={profileErrors.lastName?.message}
                                        fullWidth
                                    />
                                )}
                            />
                            <Controller
                                name="countryId"
                                control={profileControl}
                                render={({ field }) => (
                                    <FormControl fullWidth error={!!profileErrors.countryId}>
                                        <Autocomplete
                                            {...field}
                                            options={countries}
                                            getOptionLabel={(option) => `${option.telephone_code} ${option.label}`}
                                            value={countries.find((country) => country.value === field.value) || null}
                                            onChange={(_, newValue) => field.onChange(newValue ? newValue.value : '')}
                                            disabled={!isEditing}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label={
                                                        <span>
                                                            Country
                                                            <span style={{ color: 'red' }}> *</span>
                                                        </span>
                                                    }
                                                    error={!!profileErrors.countryId}
                                                    helperText={profileErrors.countryId?.message}
                                                />
                                            )}
                                            isOptionEqualToValue={(option, value) => option.value === value}
                                        />
                                    </FormControl>
                                )}
                            />
                            <Controller
                                name="phoneNo"
                                control={profileControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        type='Number'
                                        label={
                                            <span>
                                                Phone Number
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        disabled={!isEditing}
                                        error={!!profileErrors.phoneNo}
                                        helperText={profileErrors.phoneNo?.message}
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>
                        <Box className='my-5'>
                            <Controller
                                name="email"
                                control={profileControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label="Email"
                                        type="email"
                                        disabled={true}
                                        error={!!profileErrors.email}
                                        helperText={profileErrors.email?.message}
                                        fullWidth
                                    />
                                )}
                            />
                        </Box>
                        {isEditing &&
                            (<>
                                <button
                                    onClick={() => handleProfileSubmit(onProfileSubmit)()}
                                    type="submit"
                                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg mr-5"
                                >
                                    Save
                                </button>

                                {/* <Button
                                    type="button"
                                    variant="outlined"
                                    color="error"
                                    onClick={handleReset}
                                >
                                    Reset
                                </Button> */}
                            </>

                            )}
                    </CardContent>
                </Card>
            )}

            {tabValue === 1 && (
                <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
                    <CardHeader
                        title={
                            <Box display="flex" alignItems="center" gap={1}>
                                <Lock />
                                <Typography variant="h6" fontWeight="bold">Reset Password</Typography>
                            </Box>
                        }
                        sx={{ color: 'white', borderRadius: '8px 8px 0 0', p: 2 }}
                        className='bg-green-600 text-white'
                    />
                    <Divider />
                    <CardContent sx={{ p: 3 }}>
                        <Box component="form" onSubmit={handlePasswordSubmit(onPasswordSubmit)}>
                            <Controller
                                name="currentPassword"
                                control={passwordControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                Current Password
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        type={showPassword.currentPassword ? 'text' : 'password'}
                                        error={!!passwordErrors.currentPassword}
                                        helperText={passwordErrors.currentPassword?.message}
                                        fullWidth
                                        sx={{ mb: 3 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => handleTogglePassword('currentPassword')}
                                                        edge="end"
                                                    >
                                                        {showPassword.currentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="newPassword"
                                control={passwordControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                New Password
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        type={showPassword.newPassword ? 'text' : 'password'}
                                        error={!!passwordErrors.newPassword}
                                        helperText={passwordErrors.newPassword?.message}
                                        fullWidth
                                        sx={{ mb: 3 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => handleTogglePassword('newPassword')}
                                                        edge="end"
                                                    >
                                                        {showPassword.newPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <Controller
                                name="confirmPassword"
                                control={passwordControl}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        label={
                                            <span>
                                                Confirm New Password
                                                <span style={{ color: 'red' }}> *</span>
                                            </span>
                                        }
                                        type={showPassword.confirmPassword ? 'text' : 'password'}
                                        error={!!passwordErrors.confirmPassword}
                                        helperText={passwordErrors.confirmPassword?.message}
                                        fullWidth
                                        sx={{ mb: 3 }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={() => handleTogglePassword('confirmPassword')}
                                                        edge="end"
                                                    >
                                                        {showPassword.confirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                            <button
                                type="submit"
                                variant="contained"
                                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg mr-5"
                            >
                                Reset Password
                            </button>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default MyProfile;